import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Scale, AlertCircle, Shield } from 'lucide-react';

const TermsConditions = () => {
  return (
    <div className="bg-white pt-16">
      {/* Hero Section */}
      <section className="relative py-20 px-8 lg:px-16 overflow-hidden bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0f0a1a]">
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 mb-6 px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
              <FileText size={20} className="text-light-pink" />
              <span className="text-sm font-mono uppercase tracking-wider text-white">Legal</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white leading-tight tracking-tight" style={{ fontWeight: 700 }}>
              Terms & Conditions
            </h1>
            
            <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
              Please read these terms and conditions carefully before using our website or services.
            </p>
            
            <p className="text-sm text-white/60 mt-4">
              Last updated: January 1, 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-8 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-light-pink/20 flex items-center justify-center">
                  <Scale size={24} className="text-light-pink" />
                </div>
                <h2 className="text-3xl font-bold mb-0">Agreement to Terms</h2>
              </div>
              <p className="text-lg text-dark-grey leading-relaxed">
                By accessing and using the SYNCORE LLC website and services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use our service.
              </p>
            </motion.div>

            {/* Definitions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Definitions</h2>
              <ul className="text-lg text-dark-grey mb-6 pl-6">
                <li className="mb-3"><strong>"Company"</strong> (referred to as "we," "us," or "our") refers to SYNCORE LLC.</li>
                <li className="mb-3"><strong>"User"</strong> (referred to as "you") refers to anyone who accesses or uses our website or services.</li>
                <li className="mb-3"><strong>"Services"</strong> refers to all digital services, products, and solutions provided by SYNCORE LLC.</li>
                <li className="mb-3"><strong>"Website"</strong> refers to the SYNCORE LLC website and all associated web properties.</li>
              </ul>
            </motion.div>

            {/* Use License */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Use License</h2>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                Permission is granted to temporarily download one copy of the materials on SYNCORE LLC's website for personal, non-commercial transitory viewing only.
              </p>
              
              <h3 className="text-xl font-bold mb-4">This license shall automatically terminate if you violate any of these restrictions:</h3>
              <ul className="text-lg text-dark-grey mb-6 pl-6">
                <li className="mb-2">• You may not modify or copy the materials</li>
                <li className="mb-2">• You may not use the materials for commercial purposes</li>
                <li className="mb-2">• You may not attempt to reverse engineer any software</li>
                <li className="mb-2">• You may not remove copyright or proprietary notations</li>
              </ul>
            </motion.div>

            {/* Services Terms */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Services Terms</h2>
              
              <h3 className="text-xl font-bold mb-4">Service Agreements</h3>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                All services provided by SYNCORE LLC are subject to separate service agreements. These terms and conditions supplement but do not replace individual service contracts.
              </p>

              <h3 className="text-xl font-bold mb-4">Payment Terms</h3>
              <ul className="text-lg text-dark-grey mb-6 pl-6">
                <li className="mb-2">• Payment terms are specified in individual contracts</li>
                <li className="mb-2">• Late payments may incur additional fees</li>
                <li className="mb-2">• All prices are in USD unless otherwise specified</li>
                <li className="mb-2">• Refunds are subject to our refund policy</li>
              </ul>

              <h3 className="text-xl font-bold mb-4">Project Scope</h3>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                All project deliverables and timelines are defined in individual statements of work. Changes to project scope may affect pricing and delivery dates.
              </p>
            </motion.div>

            {/* User Responsibilities */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">User Responsibilities</h2>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                Users of our website and services agree to:
              </p>
              <ul className="text-lg text-dark-grey mb-6 pl-6">
                <li className="mb-2">• Provide accurate and complete information</li>
                <li className="mb-2">• Use services only for lawful purposes</li>
                <li className="mb-2">• Respect intellectual property rights</li>
                <li className="mb-2">• Not interfere with website operations</li>
                <li className="mb-2">• Not transmit harmful or malicious content</li>
                <li className="mb-2">• Comply with all applicable laws and regulations</li>
              </ul>
            </motion.div>

            {/* Prohibited Uses */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <AlertCircle size={24} className="text-red-500" />
                </div>
                <h2 className="text-3xl font-bold mb-0">Prohibited Uses</h2>
              </div>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                You may not use our website or services:
              </p>
              <ul className="text-lg text-dark-grey mb-6 pl-6">
                <li className="mb-2">• For any unlawful purpose or activity</li>
                <li className="mb-2">• To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
                <li className="mb-2">• To transmit or procure the sending of advertising or promotional material without our prior written consent</li>
                <li className="mb-2">• To impersonate or attempt to impersonate the Company or its employees</li>
                <li className="mb-2">• To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the website</li>
              </ul>
            </motion.div>

            {/* Intellectual Property */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Intellectual Property Rights</h2>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                The service and its original content, features, and functionality are and will remain the exclusive property of SYNCORE LLC and its licensors. The service is protected by copyright, trademark, and other laws.
              </p>
              
              <h3 className="text-xl font-bold mb-4">Client Work</h3>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                Upon full payment, clients receive ownership rights to custom work created specifically for them, subject to any third-party licenses or restrictions outlined in project agreements.
              </p>
            </motion.div>

            {/* Disclaimers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Disclaimers</h2>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                The information on this website is provided on an "as is" basis. To the fullest extent permitted by law, this Company:
              </p>
              <ul className="text-lg text-dark-grey mb-6 pl-6">
                <li className="mb-2">• Excludes all representations and warranties relating to this website and its contents</li>
                <li className="mb-2">• Excludes all liability for damages arising out of or in connection with your use of this website</li>
                <li className="mb-2">• Does not guarantee uninterrupted or error-free service</li>
                <li className="mb-2">• May modify or discontinue services without notice</li>
              </ul>
            </motion.div>

            {/* Limitation of Liability */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Limitation of Liability</h2>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                In no event shall SYNCORE LLC, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the service.
              </p>
            </motion.div>

            {/* Termination */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Termination</h2>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                We may terminate or suspend your access immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.
              </p>
              <p className="text-lg text-dark-grey leading-relaxed">
                Upon termination, your right to use the service will cease immediately. If you wish to terminate your account, you may simply discontinue using the service.
              </p>
            </motion.div>

            {/* Governing Law */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Governing Law</h2>
              <p className="text-lg text-dark-grey leading-relaxed">
                These terms and conditions are governed by and construed in accordance with the laws of [State/Country] and you irrevocably submit to the exclusive jurisdiction of the courts in that state or location.
              </p>
            </motion.div>

            {/* Changes to Terms */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Changes to Terms</h2>
              <p className="text-lg text-dark-grey leading-relaxed">
                We reserve the right, at our sole discretion, to modify or replace these terms at any time. If a revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect.
              </p>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-grey rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-mid-purple/20 flex items-center justify-center">
                  <Shield size={24} className="text-mid-purple" />
                </div>
                <h2 className="text-3xl font-bold mb-0">Contact Us</h2>
              </div>
              <p className="text-lg text-dark-grey leading-relaxed mb-4">
                If you have any questions about these Terms and Conditions, please contact us:
              </p>
              <div className="text-lg text-dark-grey">
                <p className="mb-2"><strong>SYNCORE LLC</strong></p>
                <p className="mb-2">Email: info@syncore.com</p>
                <p>Address: 525 W Main St, Visalia, CA 93291, United States</p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;